import { apiClient } from '@/api/client';
import type {
  BundleUploadOutput,
  CreateDeploymentInput,
  CreateDeploymentOutput,
  CustomFieldAiSuggestion,
  CustomFieldDecisions,
  CustomFieldsImportReport,
  DeploymentActionOutput,
  DeploymentProgressOutput,
  DeploymentReportOutput,
  EnAiMatiereSuggestion,
  EnAiNiveauSuggestion,
  EnAiResponse,
  ClassLevelsImportReport,
  EngineStepReport,
  EnMappingDecisions,
  EnMatiereOption,
  EnSimpleOption,
  FieldValueJobOutput,
  ImportLogOutput,
  ItemStatus,
  MigratableDataOutput,
  NativeValuesImportReport,
  PaginatedLogs,
  PrepareResult,
  PreviewOutput,
  ReconciliationAiSuggestion,
  ReconciliationDecisions,
  ReconciliationModel,
  SchoolPeriod,
  TenantOverview,
  UpsertPreview,
  UsersFinalizeReport,
  UsersSnapshot,
} from './types';
import type { ConflictPolicyConfig } from './types';

const base = (code: string) => `/api/schools/${code}/v2-import`;

/** Normalise les réponses listing V2 (tableau, {items}, {schoolPeriods}, {data}). */
function asList<T>(data: unknown): T[] {
  if (Array.isArray(data)) return data as T[];
  if (data && typeof data === 'object') {
    const obj = data as Record<string, unknown>;
    for (const key of ['items', 'schoolPeriods', 'data', 'member']) {
      if (Array.isArray(obj[key])) return obj[key] as T[];
    }
  }
  return [];
}

export async function listSchoolPeriods(code: string): Promise<SchoolPeriod[]> {
  const res = await apiClient.get(`${base(code)}/school-periods`);
  return asList<SchoolPeriod>(res.data);
}

export async function getMigratableData(
  code: string,
  schoolPeriodId: string,
): Promise<MigratableDataOutput> {
  const res = await apiClient.get<MigratableDataOutput>(
    `${base(code)}/migratable-data/${schoolPeriodId}`,
  );
  return res.data;
}

export async function getTenantOverview(code: string): Promise<TenantOverview> {
  const res = await apiClient.get<TenantOverview>(`${base(code)}/tenant-overview`);
  return res.data;
}

/** Recherche MEF à la demande (nomenclature volumineuse) contre l'api data. */
export async function searchEnMefs(code: string, query: string): Promise<EnSimpleOption[]> {
  const res = await apiClient.get<{ items: EnSimpleOption[] }>(
    `${base(code)}/en-data/mefs/search`,
    { params: { q: query } },
  );
  return res.data.items ?? [];
}

/**
 * Matières associables à un MEF (contrainte `mef_matieres`), avec libellés.
 * Sert à scoper le choix de matière au niveau (via son MEF).
 */
export async function matieresForMef(code: string, mef: string): Promise<EnMatiereOption[]> {
  const res = await apiClient.get<{ items: EnMatiereOption[] }>(
    `${base(code)}/en-data/matieres-for-mef`,
    { params: { mef } },
  );
  return res.data.items ?? [];
}

/** Pré-mapping IA des niveaux (→ code niveau + MEF) via Gemini. */
export async function premapNiveauxAi(
  code: string,
  niveaux: string[],
): Promise<EnAiResponse<EnAiNiveauSuggestion>> {
  const res = await apiClient.post<EnAiResponse<EnAiNiveauSuggestion>>(
    `${base(code)}/ai/premap-niveaux`,
    { niveaux },
  );
  return res.data;
}

/** Pré-mapping IA des matières d'un niveau (contraintes par son MEF) via Gemini. */
export async function premapMatieresAi(
  code: string,
  mef: string,
  noms: string[],
): Promise<EnAiResponse<EnAiMatiereSuggestion>> {
  const res = await apiClient.post<EnAiResponse<EnAiMatiereSuggestion>>(
    `${base(code)}/ai/premap-matieres`,
    { mef, noms },
  );
  return res.data;
}

/**
 * Pré-mapping IA des correspondances : pour chaque valeur inconnue d'une
 * notion, propose un rapprochement avec l'existant, la création ou la mise de
 * côté.
 */
export async function premapReconciliationAi(
  code: string,
  groups: Array<{
    id: string;
    label: string;
    creatable: boolean;
    values: string[];
    candidates: string[];
  }>,
): Promise<{ enabled: boolean; suggestions: ReconciliationAiSuggestion[] }> {
  const res = await apiClient.post<{
    enabled: boolean;
    suggestions: ReconciliationAiSuggestion[];
  }>(`${base(code)}/ai/premap-reconciliation`, { groups });
  return res.data;
}

/** Pré-mapping IA des colonnes supplémentaires (→ type d'attribut V2 + libellé). */
export async function premapCustomFieldsAi(
  code: string,
  columns: Array<{ id: string; label: string; samples: string[] }>,
): Promise<{ enabled: boolean; suggestions: CustomFieldAiSuggestion[] }> {
  const res = await apiClient.post<{ enabled: boolean; suggestions: CustomFieldAiSuggestion[] }>(
    `${base(code)}/ai/premap-custom-fields`,
    { columns },
  );
  return res.data;
}

/**
 * Importe les champs personnalisés mappés (création des attributs V2 +
 * bindings par utilisateur, matching email). À appeler après l'import réel.
 */
export async function importCustomFields(
  code: string,
  files: Record<string, File>,
  mappings: CustomFieldDecisions,
  csvDelimiter: string,
  targetSchoolPeriodId: string,
  page?: { file: string; offset: number; limit: number },
): Promise<CustomFieldsImportReport> {
  const formData = new FormData();
  Object.entries(files).forEach(([logicalName, file]) => {
    formData.append(logicalName, file, `${logicalName}.csv`);
  });
  formData.append('mappings', JSON.stringify(mappings));
  formData.append('csvDelimiter', csvDelimiter);
  formData.append('targetSchoolPeriodId', targetSchoolPeriodId);
  if (page) {
    formData.append('file', page.file);
    formData.append('offset', String(page.offset));
    formData.append('limit', String(page.limit));
  }

  const res = await apiClient.post<CustomFieldsImportReport>(
    `${base(code)}/custom-fields/import`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );
  return res.data;
}

/**
 * Avancement d'un job d'import batch asynchrone de field-values V2 (champs
 * perso / valeurs natives) — à sonder jusqu'à un statut terminal.
 */
export async function getCustomFieldJob(
  code: string,
  jobId: string,
): Promise<FieldValueJobOutput> {
  const res = await apiClient.get<FieldValueJobOutput>(
    `${base(code)}/custom-fields/jobs/${jobId}`,
  );
  return res.data;
}

/**
 * Reprend les valeurs natives (colonnes telephone/adresse) que le pipeline
 * d'import V2 lit mais ne persiste jamais. À appeler après l'import réel.
 */
export async function importNativeValues(
  code: string,
  files: Record<string, File>,
  csvDelimiter: string,
  targetSchoolPeriodId: string,
  page?: { file: string; offset: number; limit: number },
): Promise<NativeValuesImportReport> {
  const formData = new FormData();
  Object.entries(files).forEach(([logicalName, file]) => {
    formData.append(logicalName, file, `${logicalName}.csv`);
  });
  formData.append('csvDelimiter', csvDelimiter);
  formData.append('targetSchoolPeriodId', targetSchoolPeriodId);
  if (page) {
    formData.append('file', page.file);
    formData.append('offset', String(page.offset));
    formData.append('limit', String(page.limit));
  }

  const res = await apiClient.post<NativeValuesImportReport>(
    `${base(code)}/native-values/import`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );
  return res.data;
}

/**
 * Exécute UNE étape du moteur d'import ADM : les fichiers sont traités côté
 * ADM (réconciliation + codes EN) et les données poussées via les API batch
 * V2 — plus d'envoi de fichiers à la V2. Étapes à enchaîner dans l'ordre du
 * catalogue.
 */
export async function runEngineStep(
  code: string,
  stepId: string,
  files: Record<string, File>,
  csvDelimiter: string,
  decisions: ReconciliationDecisions,
  enMappings: EnMappingDecisions | null,
  page?: { offset: number; limit: number },
  sessionId?: number | null,
): Promise<EngineStepReport> {
  const formData = new FormData();
  Object.entries(files).forEach(([logicalName, file]) => {
    formData.append(logicalName, file, `${logicalName}.csv`);
  });
  formData.append('stepId', stepId);
  formData.append('csvDelimiter', csvDelimiter);
  formData.append('decisions', JSON.stringify(decisions));
  if (enMappings) {
    formData.append('enMappings', JSON.stringify(enMappings));
  }
  if (page) {
    formData.append('offset', String(page.offset));
    formData.append('limit', String(page.limit));
  }
  // Historique : chaque élément poussé est tracé dans cette session.
  if (sessionId) {
    formData.append('sessionId', String(sessionId));
  }

  const res = await apiClient.post<EngineStepReport>(
    `${base(code)}/engine/run-step`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );
  return res.data;
}

/**
 * Complète les classes multi-niveaux (`code_niveau` = `CP|CE1`) — remédiation
 * d'écoles importées par l'ancien pipeline (le moteur ADM pose désormais tous
 * les niveaux à la création).
 */
export async function importClassLevels(
  code: string,
  files: Record<string, File>,
  csvDelimiter: string,
  decisions: ReconciliationDecisions,
): Promise<ClassLevelsImportReport> {
  const formData = new FormData();
  Object.entries(files).forEach(([logicalName, file]) => {
    formData.append(logicalName, file, `${logicalName}.csv`);
  });
  formData.append('csvDelimiter', csvDelimiter);
  formData.append('decisions', JSON.stringify(decisions));

  const res = await apiClient.post<ClassLevelsImportReport>(
    `${base(code)}/class-levels/import`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );
  return res.data;
}

/**
 * Photographie les utilisateurs du tenant AVANT l'import réel — à renvoyer à
 * `finalizeUsers` après import pour ne désactiver que les créés.
 */
export async function snapshotUsers(code: string): Promise<UsersSnapshot> {
  const res = await apiClient.post<UsersSnapshot>(`${base(code)}/users/snapshot`);
  return res.data;
}

/**
 * Repasse en INACTIVE les utilisateurs créés par l'import (le pipeline V2 les
 * crée ACTIVE en dur ; un utilisateur importé ne doit recevoir aucune
 * invitation avant activation volontaire).
 */
export async function finalizeUsers(
  code: string,
  snapshot: UsersSnapshot,
): Promise<UsersFinalizeReport> {
  const res = await apiClient.post<UsersFinalizeReport>(`${base(code)}/users/finalize`, {
    users: snapshot.users,
  });
  return res.data;
}

/** Analyse les fichiers (gardés localement) contre l'existant V2. */
export async function analyzeFiles(
  code: string,
  files: Record<string, File>,
  csvDelimiter: string,
): Promise<ReconciliationModel> {
  const formData = new FormData();
  Object.entries(files).forEach(([logicalName, file]) => {
    formData.append(logicalName, file, `${logicalName}.csv`);
  });
  formData.append('csvDelimiter', csvDelimiter);

  const res = await apiClient.post<ReconciliationModel>(`${base(code)}/analyze`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return res.data;
}

/** Applique les décisions, génère/réécrit les fichiers, uploade le bundle final. */
export async function prepareBundle(
  code: string,
  files: Record<string, File>,
  decisions: ReconciliationDecisions,
  csvDelimiter: string,
  enMappings?: EnMappingDecisions | null,
): Promise<PrepareResult> {
  const formData = new FormData();
  Object.entries(files).forEach(([logicalName, file]) => {
    formData.append(logicalName, file, `${logicalName}.csv`);
  });
  formData.append('decisions', JSON.stringify(decisions));
  formData.append('csvDelimiter', csvDelimiter);
  if (enMappings) {
    formData.append('enMappings', JSON.stringify(enMappings));
  }

  const res = await apiClient.post<PrepareResult>(`${base(code)}/prepare`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return res.data;
}

/**
 * Aperçu « upsert » du bundle final (mêmes entrées que prepare, sans upload) :
 * pour chaque fichier, ce qui sera créé vs déjà présent dans l'école.
 * Calculé côté ADM — le dry-run V2 ne distingue pas créé/mis à jour.
 */
export async function upsertPreview(
  code: string,
  files: Record<string, File>,
  decisions: ReconciliationDecisions,
  csvDelimiter: string,
  enMappings: EnMappingDecisions | null,
  conflictPolicy: ConflictPolicyConfig,
): Promise<UpsertPreview> {
  const formData = new FormData();
  Object.entries(files).forEach(([logicalName, file]) => {
    formData.append(logicalName, file, `${logicalName}.csv`);
  });
  formData.append('decisions', JSON.stringify(decisions));
  formData.append('csvDelimiter', csvDelimiter);
  formData.append('conflictPolicy', JSON.stringify(conflictPolicy));
  if (enMappings) {
    formData.append('enMappings', JSON.stringify(enMappings));
  }

  const res = await apiClient.post<UpsertPreview>(`${base(code)}/upsert-preview`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return res.data;
}

/** Télécharge le ZIP de modèles CSV et déclenche le download navigateur. */
export async function downloadTemplates(
  code: string,
  logicalFiles: string[],
  opts?: { prefill?: boolean; sourceSchoolPeriodId?: string },
): Promise<void> {
  const params = new URLSearchParams();
  logicalFiles.forEach((f) => params.append('logicalFiles[]', f));
  if (opts?.prefill) {
    params.append('prefill', 'true');
    if (opts.sourceSchoolPeriodId) params.append('sourceSchoolPeriodId', opts.sourceSchoolPeriodId);
  }

  const res = await apiClient.get(`${base(code)}/templates?${params.toString()}`, {
    responseType: 'blob',
  });

  const url = window.URL.createObjectURL(new Blob([res.data]));
  const link = document.createElement('a');
  link.href = url;
  link.download = `modeles-import-${code}.zip`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

/**
 * Upload du bundle. `files` est indexé par nom logique (= nom de champ) :
 * c'est ce qui relie chaque fichier à son step côté V2.
 */
export async function uploadBundle(
  code: string,
  files: Record<string, File>,
): Promise<BundleUploadOutput> {
  const formData = new FormData();
  Object.entries(files).forEach(([logicalName, file]) => {
    formData.append(logicalName, file, `${logicalName}.csv`);
  });

  const res = await apiClient.post<BundleUploadOutput>(`${base(code)}/upload`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return res.data;
}

export async function createDeployment(
  code: string,
  input: CreateDeploymentInput,
): Promise<CreateDeploymentOutput> {
  const res = await apiClient.post<CreateDeploymentOutput>(`${base(code)}/deployments`, input);
  return res.data;
}

export async function executeDeployment(
  code: string,
  id: string,
): Promise<DeploymentActionOutput> {
  const res = await apiClient.post<DeploymentActionOutput>(`${base(code)}/deployments/${id}/execute`);
  return res.data;
}

export async function confirmDeployment(
  code: string,
  id: string,
): Promise<DeploymentActionOutput> {
  const res = await apiClient.post<DeploymentActionOutput>(`${base(code)}/deployments/${id}/confirm`);
  return res.data;
}

export async function resumeDeployment(code: string, id: string): Promise<DeploymentActionOutput> {
  const res = await apiClient.post<DeploymentActionOutput>(`${base(code)}/deployments/${id}/resume`);
  return res.data;
}

export async function getProgress(code: string, id: string): Promise<DeploymentProgressOutput> {
  const res = await apiClient.get<DeploymentProgressOutput>(`${base(code)}/deployments/${id}/progress`);
  return res.data;
}

export async function getReport(code: string, id: string): Promise<DeploymentReportOutput> {
  const res = await apiClient.get<DeploymentReportOutput>(`${base(code)}/deployments/${id}/report`);
  return res.data;
}

export async function getPreview(code: string, id: string): Promise<PreviewOutput> {
  const res = await apiClient.get<PreviewOutput>(`${base(code)}/deployments/${id}/preview`);
  return res.data;
}

export async function getStepLogs(
  code: string,
  id: string,
  stepId: string,
  opts?: { status?: ItemStatus; page?: number; limit?: number },
): Promise<PaginatedLogs> {
  const params = new URLSearchParams();
  if (opts?.status) params.append('status', opts.status);
  if (opts?.page) params.append('page', String(opts.page));
  if (opts?.limit) params.append('limit', String(opts.limit));

  const res = await apiClient.get(
    `${base(code)}/deployments/${id}/steps/${stepId}/logs?${params.toString()}`,
  );
  const data = res.data;
  if (Array.isArray(data)) return { items: data as ImportLogOutput[] };
  return {
    items: asList<ImportLogOutput>(data),
    total: data?.total,
    page: data?.page,
    limit: data?.limit,
    hasNext: data?.hasNext,
  };
}

export async function retryStep(
  code: string,
  id: string,
  stepId: string,
): Promise<DeploymentActionOutput> {
  const res = await apiClient.post<DeploymentActionOutput>(
    `${base(code)}/deployments/${id}/steps/${stepId}/retry`,
  );
  return res.data;
}
